import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Play,
  Pause,
  Heart,
  SpeakerHigh,
  SpeakerSimpleX,
  X,
  Broadcast,
  Moon,
  ShareNetwork,
  MusicNotes,
} from "@phosphor-icons/react";
import { usePlayer } from "../context/PlayerContext";
import { flagEmoji, initials, safeFavicon } from "../lib/utils-radio";
import { shareStation } from "../lib/share";
import { Slider } from "./ui/slider";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Equalizer } from "./Equalizer";

const SLEEP_OPTIONS = [15, 30, 45, 60, 90];

export const PlayerBar = () => {
  const {
    current,
    isPlaying,
    isLoading,
    togglePlay,
    stop,
    volume,
    setVolume,
    muted,
    setMuted,
    toggleFavorite,
    isFavorite,
    nowPlayingTitle,
    sleepEndsAt,
    setSleep,
    cancelSleep,
  } = usePlayer();

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!sleepEndsAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [sleepEndsAt]);
  const remaining = sleepEndsAt ? Math.max(0, sleepEndsAt - now) : 0;
  const remMin = Math.floor(remaining / 60000);
  const remSec = Math.floor((remaining % 60000) / 1000);

  const favicon = current ? safeFavicon(current.favicon) : null;

  return (
    <AnimatePresence>
      {current && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 30 }}
          data-testid="player-bar"
          className="fixed bottom-0 left-0 z-50 w-full border-t border-border bg-[rgba(9,9,11,0.72)] backdrop-blur-2xl shadow-[0_-8px_32px_rgba(0,0,0,0.5)]"
        >
          <div className="mx-auto flex max-w-[1600px] items-center gap-4 px-4 py-3 md:px-8">
            {/* Station info */}
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-surfaceHover">
                {favicon ? (
                  <img
                    src={favicon}
                    alt={current.name}
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                ) : null}
                <div
                  className="absolute inset-0 flex items-center justify-center font-heading text-sm font-semibold text-gold"
                  style={{ display: favicon ? "none" : "flex" }}
                >
                  {initials(current.name)}
                </div>
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="truncate font-heading text-sm font-medium text-foreground" data-testid="player-station-name">
                    {current.name?.trim()}
                  </p>
                  {isPlaying && <Equalizer active className="hidden sm:flex" />}
                </div>
                <p className="truncate font-mono text-xs text-muted2" data-testid="player-nowplaying">
                  {nowPlayingTitle ? (
                    <span className="inline-flex items-center gap-1.5">
                      <MusicNotes size={12} weight="fill" className="text-gold" />
                      {nowPlayingTitle}
                    </span>
                  ) : (
                    <>
                      <span className="text-gold">● LIVE</span> {flagEmoji(current.countrycode)}{" "}
                      {current.country || "Global"}
                    </>
                  )}
                </p>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-2">
              <button
                data-testid="player-share-btn"
                onClick={() => shareStation(current)}
                className="hidden p-2 text-muted2 transition-colors hover:text-gold sm:block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-full"
                aria-label="Share station"
              >
                <ShareNetwork size={20} />
              </button>

              <button
                data-testid="player-fav-btn"
                onClick={() => toggleFavorite(current)}
                className="hidden p-2 text-muted2 transition-colors hover:text-gold sm:block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-full"
                aria-label="Toggle favorite"
              >
                <Heart weight={isFavorite(current.stationuuid) ? "fill" : "regular"} size={20} className={isFavorite(current.stationuuid) ? "text-gold" : ""} />
              </button>

              <button
                data-testid="player-toggle-btn"
                onClick={togglePlay}
                className="flex h-12 w-12 items-center justify-center rounded-full bg-gold text-black transition-transform hover:scale-105 active:scale-95 shadow-[0_0_15px_rgba(229,178,93,0.4)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
                aria-label={isPlaying ? "Pause" : "Play"}
              >
                {isLoading ? (
                  <Broadcast size={20} className="animate-pulse" weight="fill" />
                ) : isPlaying ? (
                  <Pause weight="fill" size={20} />
                ) : (
                  <Play weight="fill" size={20} className="ml-0.5" />
                )}
              </button>
            </div>

            {/* Volume + sleep + close */}
            <div className="flex flex-1 items-center justify-end gap-2 md:gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="sleep-timer-btn"
                    className={`flex items-center gap-1.5 rounded-full p-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold ${
                      sleepEndsAt ? "text-gold" : "text-muted2 hover:text-gold"
                    }`}
                    aria-label="Sleep timer"
                  >
                    <Moon size={20} weight={sleepEndsAt ? "fill" : "regular"} />
                    {sleepEndsAt && (
                      <span className="font-mono text-xs tabular-nums" data-testid="sleep-countdown">
                        {remMin}:{String(remSec).padStart(2, "0")}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="border-border bg-surface text-foreground">
                  <DropdownMenuLabel className="font-mono text-xs text-muted2">Sleep Timer</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-border" />
                  {SLEEP_OPTIONS.map((m) => (
                    <DropdownMenuItem
                      key={m}
                      data-testid={`sleep-${m}`}
                      onClick={() => setSleep(m)}
                      className="cursor-pointer focus:bg-surfaceHover focus:text-gold"
                    >
                      {m} minutes
                    </DropdownMenuItem>
                  ))}
                  {sleepEndsAt && (
                    <>
                      <DropdownMenuSeparator className="bg-border" />
                      <DropdownMenuItem
                        data-testid="sleep-cancel"
                        onClick={cancelSleep}
                        className="cursor-pointer text-destructive focus:bg-surfaceHover focus:text-destructive"
                      >
                        Cancel timer
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              <div className="hidden w-32 items-center gap-2 md:flex">
                <button
                  onClick={() => setMuted((m) => !m)}
                  data-testid="player-mute-btn"
                  className="text-muted2 transition-colors hover:text-gold"
                  aria-label="Mute"
                >
                  {muted || volume === 0 ? <SpeakerSimpleX size={20} /> : <SpeakerHigh size={20} />}
                </button>
                <Slider
                  data-testid="player-volume-slider"
                  value={[muted ? 0 : volume * 100]}
                  max={100}
                  step={1}
                  onValueChange={(v) => {
                    setMuted(false);
                    setVolume(v[0] / 100);
                  }}
                  className="cursor-pointer"
                />
              </div>
              <button
                data-testid="player-close-btn"
                onClick={stop}
                className="p-2 text-muted2 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-full"
                aria-label="Stop"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
